import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Minus, Plus, Search, Trash2 } from 'lucide-react';
import { mutations, type CreateOrderInput } from '@/lib/mutations';
import { outbox } from '@/lib/outbox';
import {
  useListCategoriesQuery,
  useListFullCatalogQuery,
  type CatalogItemRow,
} from '@/Redux/Slices/catalogApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { localizedName } from '@/lib/localized';
import { refusedLine } from '@/lib/orderRefusal';
import { apiMessage } from '../lib/helpers';
import { branchOf, orderBodyFor, type OrderTarget } from '../lib/order-target';

interface CartLine {
  itemId: string;
  name: string;
  /** The listed price, kept as the server's string — display only. */
  unitPrice: string;
  quantity: number;
}

/**
 * A 422 from `POST /orders` — the item is sold out, or its menu is outside its
 * schedule window right now.
 *
 * This is not an error state and must not be rendered as one. A cached menu is
 * *expected* to offer something that has since gone away: the client cannot
 * know, only the server can, and it says so at the one moment that matters.
 * Reported inline, with the cart intact, so the operator fixes one line instead
 * of being told "action failed" and starting again.
 */
interface Refusal {
  /** The server's own sentence — it names the item and, for a schedule refusal,
   *  the menu and hours it *is* served on. Worth more than a generic string. */
  message: string;
  /** The cart line the message is about, when it can be matched. */
  itemId: string | null;
}

interface Props {
  /** The table, or the table-less visit, this order is being composed against. */
  target: OrderTarget;
  open: boolean;
  onClose: () => void;
  onOrdered: () => void;
}

/**
 * The product menu, opened on whatever the order is for.
 *
 * §8 — an order is composed of catalog lines and posted against its fulfillment
 * context. Prices are never sent: the client shows what it read, the server
 * prices what it charges (§38), so a stale menu cannot undercharge.
 *
 * Two contexts, one screen (`OrderTarget`):
 *
 * - A **table**. A free one gets its session opened here rather than sending
 *   the operator away to do it first — picking "add order" is the instruction.
 * - A **check**, with no table anywhere in sight. This is the kids area, the
 *   PS5 lounge, the garden bench: ADR-003 says the order carries a fulfillment
 *   context, not a table, and `POST /orders { checkId, items }` is accepted with
 *   `sessionId: null`. Nothing is opened; the lines land on that bill when the
 *   order is sent.
 */
export function QuickOrderDialog({ target, open, onClose, onOrdered }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branchId = branchOf(target);
  const { format, decimals } = useCurrency(branchId);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  /**
   * The whole menu, once — from IndexedDB on the first paint after a reload,
   * from RTK Query's in-memory cache on every open after that, revalidated
   * behind whichever answered (`@/Redux/Slices/catalogApi`).
   *
   * That is why category and search are filtered below in memory rather than
   * sent as `categoryId` / `search`: on a weak link the round trip per chip tap
   * and per keystroke *was* the wait, and the rows are already here.
   *
   * `refetchOnMountOrArgChange: 60` is the freshness floor — reopening the
   * dialog a minute later revalidates behind the grid the operator is already
   * looking at, so sold-out flags catch up without anyone watching a spinner.
   */
  const categories = useListCategoriesQuery(
    { limit: 100, isActive: true },
    { skip: !open, refetchOnMountOrArgChange: 60 },
  );
  const catalog = useListFullCatalogQuery(
    { isActive: true },
    { skip: !open, refetchOnMountOrArgChange: 60 },
  );

  const categoryRows = categories.data?.rows ?? [];

  /**
   * Category and search applied locally. Matching name-or-SKU mirrors what
   * `QueryCatalogDto` does server-side, so the same term finds the same rows
   * whether the menu came off the wire or off the disk.
   */
  const itemRows = useMemo(() => {
    const rows = catalog.data?.rows ?? [];
    const term = search.trim().toLocaleLowerCase();
    return rows.filter((item) => {
      if (categoryId && item.categoryId !== categoryId) return false;
      if (!term) return true;
      return (
        item.name.toLocaleLowerCase().includes(term) ||
        // Both, so searching "mojito" finds a product named in Arabic and vice versa.
        localizedName(item, i18n.language).toLocaleLowerCase().includes(term) ||
        (item.sku?.toLocaleLowerCase().includes(term) ?? false)
      );
    });
  }, [catalog.data, categoryId, search]);

  // Closing clears the cart: the next thing this dialog opens on is a different
  // bill, and a leftover line would be added to it.
  useEffect(() => {
    if (open) return;
    setCart([]);
    setSearch('');
    setCategoryId(null);
    setRefusal(null);
  }, [open]);

  const add = useCallback((item: CatalogItemRow) => {
    setRefusal(null);
    setCart((lines) => {
      const found = lines.find((line) => line.itemId === item.id);
      if (found) {
        return lines.map((line) =>
          line.itemId === item.id ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [
        ...lines,
        {
          itemId: item.id,
          name: localizedName(item, i18n.language),
          unitPrice: item.basePrice,
          quantity: 1,
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((itemId: string, delta: number) => {
    setRefusal(null);
    setCart((lines) =>
      lines
        .map((line) =>
          line.itemId === itemId ? { ...line, quantity: line.quantity + delta } : line,
        )
        .filter((line) => line.quantity > 0),
    );
  }, []);

  const removeLine = useCallback((itemId: string) => {
    setRefusal(null);
    setCart((lines) => lines.filter((line) => line.itemId !== itemId));
  }, []);

  /**
   * Sum in minor units: a float total must never disagree with the printed
   * bill. The scale is the branch currency's own — a hardcoded two decimals is
   * wrong for KWD (3) and wrong for JPY (0).
   */
  const total = useMemo(() => {
    const scale = 10 ** decimals;
    const minor = cart.reduce(
      (sum, line) => sum + Math.round(Number(line.unitPrice) * scale) * line.quantity,
      0,
    );
    return (minor / scale).toFixed(decimals);
  }, [cart, decimals]);

  /**
   * With no session yet the first call is `POST /sessions`, which enforces
   * `sessions.create` — `orders.create` alone is not enough to get through this
   * dialog, so the button that would 403 says so instead of trying. A check
   * target opens nothing, so it never needs that key.
   */
  const opensSession = target.kind === 'table' && !target.sessionId;
  const blocked =
    opensSession && !can('sessions.create') ? t('spaces.order.needsSessionCreate') : null;

  /**
   * `submit: true` confirms and fires in the same call, so `OrdersController`
   * charges for `orders.update` and `orders.send` on top of `orders.create`.
   * A create-only user is not offered the button that would 403 — they can
   * still save the draft, which is exactly what the server would let them do.
   */
  const canSend = can('orders.update', 'orders.send');

  const submit = useCallback(
    async (sendToKitchen: boolean) => {
      if (cart.length === 0) return;
      setBusy(true);
      try {
        // §7 — a table with no session is being opened by this very act. A
        // check needs nothing opened: it *is* the context (ADR-003).
        let sessionId = target.kind === 'table' ? target.sessionId : null;
        if (target.kind === 'table' && !sessionId) {
          const opened = await mutations.openSession({ tableId: target.table.id });
          // The order body has to name the server-assigned session. Offline the
          // open is durably queued but there is no id yet, so the operator is
          // told that rather than handed an order hung off an id that does not
          // exist — and the cart is left untouched, so nothing they picked is
          // lost while they wait for the link to come back.
          if (!opened.synced || !opened.data?.id) {
            toast.warning(t('spaces.order.queuedSession'));
            return;
          }
          sessionId = opened.data.id;
        }

        // `orderBodyFor` is the ADR-003 body builder — it decides whether this
        // order names a session or a check — and answers an untyped record, so
        // the lines are restated here to meet the typed write surface. Still no
        // price anywhere in it: the server prices what it charges (§38).
        const items = cart.map((line) => ({ itemId: line.itemId, quantity: line.quantity }));
        const placed = await mutations.createOrder({
          ...(orderBodyFor(target, {
            sessionId,
            items,
            submit: sendToKitchen,
          }) as Partial<CreateOrderInput>),
          items,
        });

        // `synced: false` covers two very different outcomes and they must not
        // be reported as one. Offline, the order is durably queued. Refused, it
        // is a dead letter — and a 422 is the refusal a cached menu makes
        // routine: the item sold out, or its menu is outside its schedule
        // window (`order-pricing.ts`, `assertMenuWindowsOpen`). Telling an
        // operator "queued" when the kitchen has actually said no is the worst
        // possible answer, because they walk away believing it is coming.
        if (!placed.synced) {
          const letter = (await outbox.dead()).find(
            (item) => item.entry.operationId === placed.operationId,
          );
          if (letter?.status === 422) {
            // Not a lost write, so it does not belong in the dead letter: the
            // cart is still on screen and the operator is about to fix it here.
            // Leaving it would sit a "1 failed action" badge over work that was
            // never in doubt.
            await outbox.discardDead(placed.operationId);
            setRefusal({
              message: letter.message,
              itemId: refusedLine(letter.message, cart)?.itemId ?? null,
            });
            // Pull a fresh menu behind the dialog so the grid stops offering it.
            void catalog.refetch();
            return;
          }
          toast.info(t('spaces.order.queued'));
        } else {
          toast.success(sendToKitchen ? t('spaces.order.sent') : t('spaces.order.saved'));
        }
        setCart([]);
        setRefusal(null);
        onOrdered();
        onClose();
      } catch (error) {
        toast.error(apiMessage(error, t('operations.errors.actionFailed')));
      } finally {
        setBusy(false);
      }
    },
    [cart, catalog, onClose, onOrdered, t, target],
  );

  const title =
    target.kind === 'table'
      ? t('spaces.order.title', { number: target.table.number })
      : t('spaces.order.checkTitle', { name: target.label });

  const description =
    target.kind === 'table'
      ? target.sessionId
        ? t('spaces.order.onSession')
        : t('spaces.order.willOpenSession')
      : (target.context ?? t('spaces.order.onCheck'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      // Fixed at 85% of the viewport rather than sized to content: this is a
      // till screen, and a panel that grows and shrinks as the search filters
      // moves the buttons under the operator's finger between taps.
      className="h-[85dvh] w-full sm:w-[85vw] max-w-none"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          {/* The server prices the order (§38) — tax and service are applied
              there, so this is the line subtotal and must not claim to be the
              total the guest will pay. */}
          <span className="text-sm">
            <span className="font-medium tabular-nums">
              {t('spaces.order.subtotal')} {format(total)}
            </span>
            <span className="ms-2 text-xs text-ink-muted">
              {t('spaces.order.taxNote')}
            </span>
            {blocked && <span className="block text-xs text-[var(--destructive)]">{blocked}</span>}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void submit(false)}
              disabled={busy || cart.length === 0 || blocked !== null}
              title={blocked ?? (cart.length === 0 ? t('spaces.order.cartEmpty') : undefined)}
            >
              {t('spaces.order.save')}
            </Button>
            {canSend && (
              <Button
                onClick={() => void submit(true)}
                disabled={busy || cart.length === 0 || blocked !== null}
                title={blocked ?? (cart.length === 0 ? t('spaces.order.cartEmpty') : undefined)}
              >
                {t('spaces.order.send')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="grid h-full min-h-0 gap-4 md:grid-cols-[1fr_340px]">
        <div className="flex min-h-0 min-w-0 flex-col">
          {/* The cache is only honest if it says so. These rows may be days
              old — which is safe for ordering, because no price on this screen
              is ever sent (§38), but not safe to pass off as live. */}
          {catalog.data?.fromCache && (
            <p className="mb-2 text-xs text-ink-muted">
              {t('spaces.order.cachedMenu', {
                when: new Date(catalog.data.fetchedAt).toLocaleString(i18n.language),
              })}
            </p>
          )}
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute inset-inline-start-3 top-2.5 h-4 w-4 text-ink-muted" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('spaces.order.search')}
              aria-label={t('spaces.order.search')}
            />
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={categoryId === null ? 'default' : 'secondary'}
              onClick={() => setCategoryId(null)}
            >
              {t('spaces.order.allCategories')}
            </Button>
            {categoryRows.map((category) => (
              <Button
                key={category.id}
                size="sm"
                variant={categoryId === category.id ? 'default' : 'secondary'}
                onClick={() => setCategoryId(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pe-1">
            {catalog.isLoading ? (
              <p className="py-8 text-center text-sm text-ink-muted">
                {t('common.loading')}
              </p>
            ) : catalog.isError && !catalog.data ? (
              // Both the network and the disk came up empty. "No items match"
              // would be a lie — there is a menu, it just is not here.
              <p className="py-8 text-center text-sm text-[var(--destructive)]">
                {t('spaces.order.menuUnavailable')}
              </p>
            ) : itemRows.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-muted">
                {t('spaces.order.noItems')}
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {itemRows.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    disabled={item.isSoldOut}
                    onClick={() => add(item)}
                    className="flex min-h-[104px] flex-col justify-between gap-2 rounded-xl border-2 border-[var(--border)] p-4 text-start transition active:scale-[0.98] hover:border-[var(--primary)] disabled:opacity-50"
                  >
                    {/* S-15 — a photo where it earns its place. Only when the
                        item has one: a placeholder on every other tile would
                        cost the grid more height than it returns, and the
                        object is an immutable ~60 KB WebP cached for a year, so
                        122 of them are one cold load and nothing after. */}
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt=""
                        loading="lazy"
                        className="h-20 w-full shrink-0 rounded-lg object-cover"
                      />
                    )}
                    <span className="line-clamp-2 text-base font-medium leading-snug">
                      {localizedName(item, i18n.language)}
                    </span>
                    <span className="shrink-0 text-base font-semibold tabular-nums text-ink-muted">
                      {item.isSoldOut ? (
                        <Badge tone="warning">{t('spaces.order.soldOut')}</Badge>
                      ) : (
                        format(item.basePrice)
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-col rounded-lg border border-[var(--border)] p-3">
          {/* The server refused one line (§38 / menu windows). Deliberately not
              styled as an error: this is what a cache that is working looks
              like from the inside, and the operator's next move is to swap one
              item, not to worry that the till is broken. */}
          {refusal && (
            <div
              role="status"
              className="mb-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-xs text-amber-900 dark:text-amber-200"
            >
              <p className="font-medium">{t('spaces.order.unavailableTitle')}</p>
              <p className="mt-1">{refusal.message}</p>
              <p className="mt-1 text-amber-800 dark:text-amber-300">{t('spaces.order.unavailableHint')}</p>
              {refusal.itemId && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => removeLine(refusal.itemId as string)}
                >
                  {t('spaces.order.unavailableRemove')}
                </Button>
              )}
            </div>
          )}

          <p className="mb-2 text-sm font-medium">{t('spaces.order.cart')}</p>
          {cart.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('spaces.order.cartEmpty')}</p>
          ) : (
            <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
              {cart.map((line) => (
                <li
                  key={line.itemId}
                  className={
                    refusal?.itemId === line.itemId
                      ? 'flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200'
                      : 'flex items-center gap-2 text-sm'
                  }
                >
                  <span className="min-w-0 flex-1 truncate">{line.name}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('spaces.order.decrease')}
                    onClick={() => setQuantity(line.itemId, -1)}
                  >
                    {line.quantity === 1 ? (
                      <Trash2 className="h-3.5 w-3.5" />
                    ) : (
                      <Minus className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <span className="w-5 text-center tabular-nums">{line.quantity}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('spaces.order.increase')}
                    onClick={() => setQuantity(line.itemId, 1)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
}
